import React, { useEffect, useState, useRef } from 'react';
import { Globe } from 'lucide-react';
import { languages } from '../i18n';
import { useLanguage } from '../hooks/useLanguage';

const LanguageSwitcher: React.FC = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lng: string) => {
    changeLanguage(lng);
    setShowLanguageDropdown(false);
  };

  // Get current language display name
  const getCurrentLanguageDisplay = () => {
    return languages[currentLanguage as keyof typeof languages]?.nativeName || currentLanguage.toUpperCase();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
        className="flex items-center gap-2 bg-white border-none px-0.5 py-0.5"
      >
        <Globe className="h-4 w-4 text-black" />
        <span className="text-sm text-black">{getCurrentLanguageDisplay()}</span>
      </button>
      
      {showLanguageDropdown && (
        <div className="absolute top-full mt-2 right-0 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="py-2">
            {Object.keys(languages).map((lng) => {
              const isSelected = currentLanguage === lng;
              return (
                <button
                  key={lng}
                  onClick={() => handleLanguageChange(lng)}
                  className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between border-none ${
                    isSelected ? 'bg-white text-black' : 'bg-white text-gray-700'
                  }`}
                >
                  <span>{languages[lng as keyof typeof languages].nativeName}</span>
                  {isSelected && (
                    <div className="w-2 h-2 bg-black rounded-full"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher; 